import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Award, Plus, Pencil, Trash2, Gift, TrendingUp, Users, Star,
  Minus, Search, Zap, CalendarRange, Sparkles,
  PartyPopper, Ticket, RotateCcw, CheckCircle2, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { CUSTOM_EXTRA_CATALOG, PREDEFINED_EXTRAS } from '../../constants/bookingCatalog';
import { AdjustPointsDialog } from '../../components/AdjustPointsDialog';
import { TabCard } from '../../components/TabCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FREEBIE_OPTIONS = [...PREDEFINED_EXTRAS, ...CUSTOM_EXTRA_CATALOG];

const emptyReward = {
  type: 'discount', name: '', description: '',
  points_cost: 500, discount_value: 25, discount_type: 'fixed',
  freebie_extra_id: '', order: 0, active: true,
};

export const CrmLoyalty = () => {
  const [rewards, setRewards] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Reward modal
  const [rewardOpen, setRewardOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyReward);
  const [saving, setSaving] = useState(false);

  // Adjust points modal
  const [adjOpen, setAdjOpen] = useState(false);
  const [presetCustomer, setPresetCustomer] = useState(null);
  const [custPage, setCustPage] = useState(0);

  // Accordion tab state (persisted in URL ?tab=)
  const [searchParams, setSearchParams] = useSearchParams();
  const openTab = searchParams.get('tab') || 'rates';
  const setTab = (k) => {
    const p = new URLSearchParams(searchParams);
    if (openTab === k) p.delete('tab'); else p.set('tab', k);
    setSearchParams(p, { replace: true });
  };

  // Earning rate + promotions
  const [settings, setSettings] = useState(null);
  const [baseRate, setBaseRate] = useState('');
  const [savingRate, setSavingRate] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoEditing, setPromoEditing] = useState(null);
  const [promoForm, setPromoForm] = useState({ name: '', points_per_dollar: 2, start_date: '', end_date: '', active: true });
  const [savingPromo, setSavingPromo] = useState(false);

  // Add-on rewards + redemptions
  const [addonRewards, setAddonRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [costDrafts, setCostDrafts] = useState({});
  const [addonBusy, setAddonBusy] = useState(null);

  // Redeem an add-on for a customer (on behalf)
  const [rfcOpen, setRfcOpen] = useState(false);
  const [rfcEmail, setRfcEmail] = useState('');
  const [rfcCtx, setRfcCtx] = useState(null);
  const [rfcBooking, setRfcBooking] = useState('');
  const [rfcReward, setRfcReward] = useState('');
  const [rfcLoading, setRfcLoading] = useState(false);
  const [rfcBusy, setRfcBusy] = useState(false);

  const addonImg = (url) => (!url ? null : url.startsWith('http') ? url : `${API_URL}${url}`);

  const fetchAll = async () => {
    try {
      const [r, c, s, ar, rd] = await Promise.all([
        axios.get(`${API_URL}/api/admin/loyalty/rewards`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/loyalty/customers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/loyalty/settings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/loyalty/addon-rewards`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/loyalty/redemptions?status=applied`, { withCredentials: true }),
      ]);
      setRewards(r.data || []);
      setCustomers(c.data || []);
      setSettings(s.data || null);
      setBaseRate(String(s.data?.base_points_per_dollar ?? 2));
      setAddonRewards(ar.data || []);
      setCostDrafts(Object.fromEntries((ar.data || []).map((x) => [x.reward_id, String(x.points_cost)])));
      setRedemptions(rd.data || []);
    } catch { toast.error('Failed to load loyalty data'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const saveAddonCost = async (r) => {
    const v = parseInt(costDrafts[r.reward_id], 10);
    if (!(v >= 0)) { toast.error('Points cost must be 0 or more'); return; }
    setAddonBusy(r.reward_id);
    try {
      await axios.put(`${API_URL}/api/admin/loyalty/addon-rewards/${r.reward_id}`, { points_cost: v }, { withCredentials: true });
      toast.success(`${r.name} set to ${v.toLocaleString()} pts`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setAddonBusy(null); }
  };

  const toggleAddon = async (r) => {
    setAddonBusy(r.reward_id);
    try {
      await axios.put(`${API_URL}/api/admin/loyalty/addon-rewards/${r.reward_id}`, { active: !r.active }, { withCredentials: true });
      toast.success(`${r.name} ${!r.active ? 'enabled' : 'disabled'}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAddonBusy(null); }
  };

  const cancelRedemption = async (red) => {
    if (!window.confirm(`Cancel "${red.addon_name}" for ${red.user_email}? ${red.points_cost} points will be refunded and the customer notified.`)) return;
    setAddonBusy(red.redemption_id);
    try {
      await axios.delete(`${API_URL}/api/admin/loyalty/redemptions/${red.redemption_id}`, { withCredentials: true });
      toast.success(`Refunded ${red.points_cost} pts and notified the customer`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
    finally { setAddonBusy(null); }
  };

  const openRfc = () => {
    setRfcEmail(''); setRfcCtx(null); setRfcBooking(''); setRfcReward('');
    setRfcOpen(true);
  };
  const loadRfc = async () => {
    const email = rfcEmail.trim().toLowerCase();
    if (!email) { toast.error('Enter a customer email'); return; }
    setRfcLoading(true); setRfcCtx(null); setRfcBooking(''); setRfcReward('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/loyalty/customer/${encodeURIComponent(email)}/addon-context`, { withCredentials: true });
      if (!res.data?.has_account) { toast.error('No customer account found for that email'); }
      setRfcCtx(res.data);
      setRfcBooking(res.data?.eligible_bookings?.[0]?.booking_id || '');
    } catch (e) { toast.error(e.response?.data?.detail || 'Lookup failed'); }
    finally { setRfcLoading(false); }
  };
  const submitRfc = async () => {
    if (!rfcBooking) { toast.error('Pick a booking'); return; }
    if (!rfcReward) { toast.error('Pick an add-on'); return; }
    setRfcBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/loyalty/redeem-addon-for-customer`,
        { email: rfcEmail.trim().toLowerCase(), reward_id: rfcReward, booking_id: rfcBooking },
        { withCredentials: true });
      toast.success('Reward applied on behalf of the customer');
      setRfcOpen(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to redeem'); }
    finally { setRfcBusy(false); }
  };

  const saveBaseRate = async () => {
    const v = parseFloat(baseRate);
    if (!(v > 0)) { toast.error('Rate must be a positive number'); return; }
    setSavingRate(true);
    try {
      await axios.put(`${API_URL}/api/admin/loyalty/settings`, { points_per_dollar: v }, { withCredentials: true });
      toast.success(`Base rate set to ${v} pt${v === 1 ? '' : 's'} per $1`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update rate'); }
    finally { setSavingRate(false); }
  };

  const openCreatePromo = () => {
    setPromoEditing(null);
    setPromoForm({ name: '', points_per_dollar: 2, start_date: '', end_date: '', active: true });
    setPromoOpen(true);
  };
  const applyPromoTemplate = (key) => {
    const base = Number(settings?.base_points_per_dollar) || 1;
    const today = new Date();
    const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let name = '', rate = base * 2, start = toISO(today), end = toISO(today);
    if (key === 'weekend') {
      const daysUntilSat = (6 - today.getDay() + 7) % 7;
      const sat = new Date(today); sat.setDate(today.getDate() + daysUntilSat);
      const sun = new Date(sat); sun.setDate(sat.getDate() + 1);
      name = 'Double Points Weekend'; rate = base * 2; start = toISO(sat); end = toISO(sun);
    } else if (key === 'birthday') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      name = 'Birthday Month Bonus'; rate = base * 2; start = toISO(first); end = toISO(last);
    } else if (key === 'triple') {
      name = 'Triple Points Day'; rate = base * 3; start = toISO(today); end = toISO(today);
    }
    setPromoEditing(null);
    setPromoForm({ name, points_per_dollar: rate, start_date: start, end_date: end, active: true });
    setPromoOpen(true);
  };
  const openEditPromo = (p) => {
    setPromoEditing(p);
    setPromoForm({
      name: p.name, points_per_dollar: p.points_per_dollar,
      start_date: p.start_date, end_date: p.end_date, active: p.active !== false,
    });
    setPromoOpen(true);
  };
  const savePromo = async () => {
    if (!promoForm.name) { toast.error('Promotion name is required'); return; }
    if (!(parseFloat(promoForm.points_per_dollar) > 0)) { toast.error('Rate must be a positive number'); return; }
    if (!promoForm.start_date || !promoForm.end_date) { toast.error('Start and end dates are required'); return; }
    if (promoForm.end_date < promoForm.start_date) { toast.error('End date must be on or after start date'); return; }
    setSavingPromo(true);
    const payload = { ...promoForm, points_per_dollar: parseFloat(promoForm.points_per_dollar) };
    try {
      if (promoEditing) {
        await axios.patch(`${API_URL}/api/admin/loyalty/promotions/${promoEditing.promo_id}`, payload, { withCredentials: true });
        toast.success('Promotion updated');
      } else {
        await axios.post(`${API_URL}/api/admin/loyalty/promotions`, payload, { withCredentials: true });
        toast.success('Promotion created');
      }
      setPromoOpen(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save promotion'); }
    finally { setSavingPromo(false); }
  };
  const deletePromo = async (p) => {
    if (!window.confirm(`Delete promotion "${p.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/loyalty/promotions/${p.promo_id}`, { withCredentials: true });
      toast.success('Promotion deleted'); fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyReward); setRewardOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      type: r.type, name: r.name, description: r.description || '',
      points_cost: r.points_cost, discount_value: r.discount_value || 0,
      discount_type: r.discount_type || 'fixed',
      freebie_extra_id: r.freebie_extra_id || '',
      order: r.order || 0, active: !!r.active,
    });
    setRewardOpen(true);
  };

  const saveReward = async () => {
    if (!form.name || !form.points_cost) { toast.error('Name and points cost required'); return; }
    if (form.type === 'discount' && !form.discount_value) { toast.error('Discount value required'); return; }
    if (form.type === 'freebie' && !form.freebie_extra_id) { toast.error('Pick a free item'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API_URL}/api/admin/loyalty/rewards/${editing.reward_id}`, form, { withCredentials: true });
        toast.success('Reward updated');
      } else {
        await axios.post(`${API_URL}/api/admin/loyalty/rewards`, form, { withCredentials: true });
        toast.success('Reward created');
      }
      setRewardOpen(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteReward = async (r) => {
    if (!window.confirm(`Delete reward "${r.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/loyalty/rewards/${r.reward_id}`, { withCredentials: true });
      toast.success('Deleted'); fetchAll();
    } catch { toast.error('Failed'); }
  };

  const submitAdjust = null; // legacy noop — replaced by AdjustPointsDialog component

  const filteredCustomers = customers.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [c.email, c.name, c.phone].some((f) => (f || '').toLowerCase().includes(q));
  });

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;

  return (
    <div>
      {/* Pinned header */}
      <div className="flex justify-between items-start mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1E1E24] flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <Award className="w-7 h-7 text-[#FF3B6B]" /> Loyalty Program
          </h1>
          <p className="text-[#6B6B7A] text-sm">Open a section below to manage it. Changes sync live.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold ${settings?.active_source === 'promo' ? 'bg-[#FF3B6B]/10 text-[#FF3B6B]' : 'bg-[#1E1E24]/5 text-[#1E1E24]'}`} data-testid="header-active-rate">
            <Zap className="w-4 h-4" /> {settings?.active_rate ?? 2} pts / $1
          </span>
          <Button onClick={() => { setPresetCustomer(null); setAdjOpen(true); }} variant="outline" className="rounded-full border-[#E8E5DF] min-h-[44px]" data-testid="adjust-points-btn">
            <Plus className="w-4 h-4 mr-0.5" /><Minus className="w-4 h-4 mr-1" />Adjust Points
          </Button>
        </div>
      </div>

      <div className="space-y-3" data-testid="crm-loyalty-accordion">

      {/* Earning Rate & Promotions */}
      <TabCard
        id="rates" open={openTab === 'rates'} onToggle={() => setTab('rates')}
        icon={<Zap className="w-5 h-5 text-[#E8A80E]" />} accent="bg-[#FFF4E6]"
        title="Earning Rate & Promotions" subtitle="Base rate, active rate and scheduled promos"
        badge={`${settings?.active_rate ?? 2} pts/$1`} badgeTone={settings?.active_source === 'promo' ? 'green' : 'pink'}
      >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="loyalty-rate-section">
        {/* Active rate at a glance */}
        <div className={`rounded-2xl p-5 text-white ${settings?.active_source === 'promo' ? 'bg-gradient-to-br from-[#FF3B6B] to-[#FB923C]' : 'bg-gradient-to-br from-[#1E1E24] to-[#3A3A44]'}`} data-testid="active-rate-card">
          <p className="text-[11px] uppercase tracking-wider opacity-80 flex items-center gap-1">
            <Zap className="w-3.5 h-3.5" /> Current active rate
          </p>
          <p className="text-4xl font-bold mt-2" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="active-rate-value">
            {settings?.active_rate ?? 2} <span className="text-lg font-medium opacity-80">pt{(settings?.active_rate === 1) ? '' : 's'} / $1</span>
          </p>
          {settings?.active_source === 'promo' && settings?.active_promo ? (
            <p className="text-xs mt-2 opacity-90" data-testid="active-promo-note">
              🎉 Promotion live: <b>{settings.active_promo.name}</b>
              {settings.active_promo.end_date ? ` · until ${settings.active_promo.end_date}` : ''}
            </p>
          ) : (
            <p className="text-xs mt-2 opacity-80">Standard base rate is active.</p>
          )}
        </div>

        {/* Base rate editor */}
        <div className="rounded-2xl border border-[#E8E5DF] bg-white p-5" data-testid="base-rate-card">
          <p className="text-sm font-semibold text-[#1E1E24] flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#FF3B6B]" /> Base earning rate</p>
          <p className="text-xs text-[#6B6B7A] mt-1">Points a customer earns per $1 spent (applies when no promotion is active).</p>
          <div className="flex items-end gap-2 mt-3">
            <div className="flex-1">
              <Label className="text-xs font-semibold text-[#6B6B7A]">Points per $1</Label>
              <Input type="number" min="0.1" step="0.1" value={baseRate} onChange={(e) => setBaseRate(e.target.value)} className="mt-1 rounded-xl" data-testid="base-rate-input" />
            </div>
            <Button onClick={saveBaseRate} disabled={savingRate} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="base-rate-save">
              {savingRate ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Promotions summary */}
        <div className="rounded-2xl border border-[#E8E5DF] bg-white p-5 flex flex-col" data-testid="promotions-card">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-[#1E1E24] flex items-center gap-2"><CalendarRange className="w-4 h-4 text-[#FB923C]" /> Promotions</p>
            <Button size="sm" onClick={openCreatePromo} className="h-7 rounded-full bg-[#1E1E24] hover:bg-black text-white text-xs" data-testid="create-promo-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> New
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="promo-templates">
            <span className="text-[10px] text-[#6B6B7A] self-center mr-0.5">Quick launch:</span>
            <button type="button" onClick={() => applyPromoTemplate('weekend')} className="text-[10px] font-semibold rounded-full border border-[#FFD9A8] bg-[#FFF4E6] text-[#946C00] px-2 py-1 hover:bg-[#FFE9CC]" data-testid="promo-template-weekend">⚡ Double Points Weekend</button>
            <button type="button" onClick={() => applyPromoTemplate('birthday')} className="text-[10px] font-semibold rounded-full border border-[#FBC6D6] bg-[#FFF0F4] text-[#B83258] px-2 py-1 hover:bg-[#FFE1EA]" data-testid="promo-template-birthday">🎂 Birthday Month Bonus</button>
            <button type="button" onClick={() => applyPromoTemplate('triple')} className="text-[10px] font-semibold rounded-full border border-[#C7D2FE] bg-[#EEF2FF] text-[#4F46E5] px-2 py-1 hover:bg-[#E0E7FF]" data-testid="promo-template-triple">✨ Triple Points Day</button>
          </div>
          <div className="mt-3 space-y-2 overflow-y-auto max-h-44" data-testid="promotions-list">
            {(settings?.promotions || []).filter((p) => p.status !== 'expired').length === 0 ? (
              <p className="text-xs text-[#6B6B7A] italic">No upcoming or live promotions. Schedule one to boost bookings.</p>
            ) : (
              (settings?.promotions || []).map((p) => {
                const badge = {
                  live: 'bg-[#06D6A0]/15 text-[#058B69]', scheduled: 'bg-[#6366F1]/15 text-[#4F46E5]',
                  paused: 'bg-[#E8A80E]/15 text-[#946C00]', expired: 'bg-gray-100 text-gray-400',
                }[p.status] || 'bg-gray-100 text-gray-500';
                return (
                  <div key={p.promo_id} className={`flex items-center justify-between gap-2 p-2 rounded-xl border border-[#E8E5DF] ${p.status === 'expired' ? 'opacity-60' : ''}`} data-testid={`promo-row-${p.promo_id}`}>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[#1E1E24] truncate flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-[#FB923C]" /> {p.name}
                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${badge}`}>{p.status}</span>
                      </p>
                      <p className="text-[10px] text-[#6B6B7A]">{p.points_per_dollar} pts/$1 · {p.start_date} → {p.end_date}</p>
                    </div>
                    <div className="flex gap-1 flex-none">
                      <Button size="sm" variant="ghost" onClick={() => openEditPromo(p)} className="h-6 w-6 p-0" data-testid={`edit-promo-${p.promo_id}`}><Pencil className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deletePromo(p)} className="h-6 w-6 p-0 text-red-500" data-testid={`delete-promo-${p.promo_id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      </TabCard>

      {/* Rewards Catalog */}
      <TabCard
        id="rewards" open={openTab === 'rewards'} onToggle={() => setTab('rewards')}
        icon={<Gift className="w-5 h-5 text-[#FF3B6B]" />} accent="bg-[#FF3B6B]/10"
        title="Rewards Catalog" subtitle="Discounts & free items customers redeem with points"
        badge={rewards.length || null}
      >
      <div className="flex justify-end mb-3">
        <Button onClick={openCreate} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full min-h-[40px]" data-testid="create-reward-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Reward
        </Button>
      </div>
      {rewards.length === 0 ? (
        <p className="text-sm text-[#6B6B7A] italic">No rewards yet — create your first reward.</p>
      ) : (
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(210px,1fr))]">
        {rewards.map((r) => (
          <div key={r.reward_id} className={`bg-white rounded-xl border p-3 ${r.active ? 'border-[#E8E5DF]' : 'border-gray-200 opacity-70'}`} data-testid={`reward-card-${r.reward_id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${r.type === 'discount' ? 'bg-[#FF3B6B]/10 text-[#FF3B6B]' : 'bg-[#06D6A0]/10 text-[#06D6A0]'}`}>
                {r.type === 'discount' ? <TrendingUp className="w-4 h-4" /> : <Gift className="w-4 h-4" />}
              </div>
              <div className="flex gap-0.5">
                <Button size="sm" variant="ghost" onClick={() => openEdit(r)} className="h-7 w-7 p-0" data-testid={`edit-reward-${r.reward_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => deleteReward(r)} className="h-7 w-7 p-0 text-red-500" data-testid={`delete-reward-${r.reward_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <p className="font-semibold text-sm text-[#1E1E24] truncate" title={r.name}>{r.name}</p>
            {r.description && <p className="text-[11px] text-[#6B6B7A] line-clamp-1 mt-0.5">{r.description}</p>}
            <div className="mt-2 flex items-center justify-between text-[11px]">
              <span className="inline-flex items-center gap-1 font-semibold text-[#1E1E24]"><Star className="w-3 h-3 text-[#E8A80E]" /> {r.points_cost} pts</span>
              <span className="text-[#6B6B7A] truncate ml-2">
                {r.type === 'discount'
                  ? `${r.discount_type === 'percent' ? r.discount_value + '%' : '$' + r.discount_value} off`
                  : `Free ${r.freebie_extra_id}`}
              </span>
            </div>
          </div>
        ))}
      </div>
      )}
      </TabCard>

      {/* Redeemable Add-ons */}
      <TabCard
        id="addons" open={openTab === 'addons'} onToggle={() => setTab('addons')}
        icon={<PartyPopper className="w-5 h-5 text-[#FB923C]" />} accent="bg-[#FB923C]/10"
        title="Redeemable Add-ons" subtitle="Add-ons customers unlock with points on an upcoming party"
        badge={addonRewards.length || null} badgeTone="amber"
      >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2" data-testid="addon-rewards-section">
        <p className="text-xs text-[#6B6B7A] flex-1 min-w-[180px]">Edit each add-on's point cost or turn it on/off. Customers redeem these free onto an upcoming party.</p>
        <Button onClick={openRfc} variant="outline" className="rounded-full border-[#E8E5DF] min-h-[40px]" data-testid="rfc-open-btn">
          <Gift className="w-4 h-4 mr-1.5" /> Redeem for a customer
        </Button>
      </div>
      {addonRewards.length === 0 ? (
        <p className="text-sm text-[#6B6B7A] italic">No add-ons found in the catalog yet.</p>
      ) : (
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(230px,1fr))]">
          {addonRewards.map((a) => {
            const src = addonImg(a.image_url);
            return (
              <div key={a.reward_id} className={`bg-white rounded-xl border p-2.5 ${a.active ? 'border-[#E8E5DF]' : 'border-gray-200 opacity-70'}`} data-testid={`addon-reward-row-${a.reward_id}`}>
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-lg bg-[#FFF4E6] flex items-center justify-center overflow-hidden flex-none">
                    {src ? <img src={src} alt={a.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <Gift className="w-4 h-4 text-[#FB923C]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#1E1E24] truncate" title={a.name}>{a.name}</p>
                    <p className="text-[11px] text-[#6B6B7A]">${(a.dollar_value || 0).toFixed(2)} value{!a.available && <span className="text-red-500"> · n/a</span>}</p>
                  </div>
                  <Switch checked={a.active} onCheckedChange={() => toggleAddon(a)} data-testid={`addon-active-${a.reward_id}`} />
                </div>
                <div className="mt-2 flex items-end gap-1.5">
                  <div className="flex-1">
                    <Label className="text-[10px] font-semibold text-[#6B6B7A]">Points cost</Label>
                    <Input type="number" min="0" value={costDrafts[a.reward_id] ?? ''} onChange={(e) => setCostDrafts((p) => ({ ...p, [a.reward_id]: e.target.value }))} className="mt-0.5 rounded-lg h-8 text-sm" data-testid={`addon-cost-input-${a.reward_id}`} />
                  </div>
                  <Button size="sm" onClick={() => saveAddonCost(a)} disabled={addonBusy === a.reward_id} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white h-8 px-3" data-testid={`addon-cost-save-${a.reward_id}`}>Save</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </TabCard>

      {/* Active Add-on Redemptions */}
      <TabCard
        id="redemptions" open={openTab === 'redemptions'} onToggle={() => setTab('redemptions')}
        icon={<Ticket className="w-5 h-5 text-[#6366F1]" />} accent="bg-[#6366F1]/10"
        title="Active Add-on Redemptions" subtitle="Rewards currently applied to customers' bookings"
        badge={redemptions.length || null} badgeTone="indigo"
      >
      <div data-testid="addon-redemptions-section">
      {redemptions.length === 0 ? (
        <p className="text-sm text-[#6B6B7A] italic">No add-on rewards are currently applied to bookings.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden mb-10" data-testid="redemptions-list">
          {redemptions.map((red) => (
            <div key={red.redemption_id} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E8E5DF] last:border-b-0" data-testid={`redemption-row-${red.redemption_id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#1E1E24] truncate flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#058B69]" /> {red.addon_name}
                  <span className="text-[#6B6B7A] font-normal">· {red.points_cost} pts</span>
                </p>
                <p className="text-xs text-[#6B6B7A] truncate">
                  {red.user_email} · {(red.child_name || 'party')}'s party ({red.booking_reference || red.booking_id}){red.booking_date ? ` · ${red.booking_date}` : ''}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => cancelRedemption(red)} disabled={addonBusy === red.redemption_id} className="rounded-full text-[#FF3B6B] hover:bg-[#FFE9EE] flex-none" data-testid={`cancel-redemption-${red.redemption_id}`}>
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Cancel & refund
              </Button>
            </div>
          ))}
        </div>
      )}
      </div>
      </TabCard>

      {/* Customer Balances */}
      <TabCard
        id="customers" open={openTab === 'customers'} onToggle={() => setTab('customers')}
        icon={<Users className="w-5 h-5 text-[#FF3B6B]" />} accent="bg-[#FF3B6B]/10"
        title="Customer Balances" subtitle="Tap a customer to adjust their points"
        badge={customers.length || null}
      >
      <div className="mb-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B7A]" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setCustPage(0); }} placeholder="Search by name, email or phone…" className="pl-9 rounded-full border-[#E8E5DF]" data-testid="customer-search" />
        </div>
      </div>
      {filteredCustomers.length === 0 ? (
        <p className="text-sm text-[#6B6B7A] italic">{search ? 'No customers match your search.' : 'No customers with points yet.'}</p>
      ) : (() => {
        const CUST_PAGE_SIZE = 10;
        const custTotalPages = Math.max(1, Math.ceil(filteredCustomers.length / CUST_PAGE_SIZE));
        const custSafePage = Math.min(custPage, custTotalPages - 1);
        const custRows = filteredCustomers.slice(custSafePage * CUST_PAGE_SIZE, custSafePage * CUST_PAGE_SIZE + CUST_PAGE_SIZE);
        return (
          <>
            <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden" data-testid="customers-list">
              {custRows.map((c) => (
                <button
                  key={c.user_id}
                  type="button"
                  onClick={() => { setPresetCustomer(c); setAdjOpen(true); }}
                  className="w-full text-left flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E8E5DF] last:border-b-0 hover:bg-[#FFFDF8] transition-colors min-h-[56px]"
                  data-testid={`customer-row-${c.user_id}`}
                  title="Adjust points for this customer"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1E1E24] truncate">{c.name || c.email}</p>
                    <p className="text-xs text-[#6B6B7A] truncate">
                      {c.email}{c.phone ? ` · ${c.phone}` : ''}
                    </p>
                  </div>
                  <span className="flex items-center gap-2 flex-none">
                    <span className="font-semibold text-[#FF3B6B]">{c.balance.toLocaleString()} pts</span>
                    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-[#6B6B7A] border border-[#E8E5DF] rounded-full px-2 py-0.5">
                      <Plus className="w-3 h-3" /><Minus className="w-3 h-3" /> Adjust
                    </span>
                  </span>
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between mt-3" data-testid="customers-pagination">
              <span className="text-xs text-[#6B6B7A]">
                Showing {custSafePage * CUST_PAGE_SIZE + 1}–{custSafePage * CUST_PAGE_SIZE + custRows.length} of {filteredCustomers.length}
              </span>
              {custTotalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" disabled={custSafePage === 0} onClick={() => setCustPage(custSafePage - 1)} className="rounded-full min-h-[40px]" data-testid="customers-prev">
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </Button>
                  <span className="text-xs text-[#6B6B7A]">Page {custSafePage + 1} of {custTotalPages}</span>
                  <Button size="sm" variant="outline" disabled={custSafePage >= custTotalPages - 1} onClick={() => setCustPage(custSafePage + 1)} className="rounded-full min-h-[40px]" data-testid="customers-next">
                    Next <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </>
        );
      })()}
      </TabCard>
      </div>

      {/* Reward Create/Edit Dialog */}
      <Dialog open={rewardOpen} onOpenChange={setRewardOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="reward-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>{editing ? 'Edit reward' : 'New reward'}</DialogTitle>
            <DialogDescription>Configure a redeemable reward for your loyalty program.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="font-semibold">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v }))}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="reward-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="discount">Discount ($ or %)</SelectItem>
                  <SelectItem value="freebie">Free add-on</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-semibold">Name</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="mt-1 rounded-xl" data-testid="reward-name" />
            </div>
            <div>
              <Label className="font-semibold">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={2} className="mt-1 rounded-xl" data-testid="reward-desc" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="font-semibold">Points cost</Label>
                <Input type="number" value={form.points_cost} onChange={(e) => setForm((p) => ({ ...p, points_cost: parseInt(e.target.value) || 0 }))} className="mt-1 rounded-xl" data-testid="reward-points" />
              </div>
              <div>
                <Label className="font-semibold">Order</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm((p) => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="mt-1 rounded-xl" />
              </div>
            </div>
            {form.type === 'discount' ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="font-semibold">Discount value</Label>
                  <Input type="number" value={form.discount_value} onChange={(e) => setForm((p) => ({ ...p, discount_value: parseFloat(e.target.value) || 0 }))} className="mt-1 rounded-xl" data-testid="reward-discount-value" />
                </div>
                <div>
                  <Label className="font-semibold">Discount type</Label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm((p) => ({ ...p, discount_type: v }))}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="fixed">Fixed $</SelectItem>
                      <SelectItem value="percent">Percent %</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div>
                <Label className="font-semibold">Free add-on</Label>
                <Select value={form.freebie_extra_id} onValueChange={(v) => setForm((p) => ({ ...p, freebie_extra_id: v }))}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="reward-freebie"><SelectValue placeholder="Pick an item…" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {FREEBIE_OPTIONS.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm bg-[#FFFDF8] rounded-xl p-3 border border-[#E8E5DF]">
              <Switch checked={form.active} onCheckedChange={(v) => setForm((p) => ({ ...p, active: v }))} />
              Active
            </label>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setRewardOpen(false)} className="flex-1 rounded-full" disabled={saving}>Cancel</Button>
              <Button onClick={saveReward} disabled={saving} className="flex-1 rounded-full bg-[#FF3B6B] text-white" data-testid="reward-save">
                {saving ? 'Saving…' : (editing ? 'Save' : 'Create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Promotion Create/Edit Dialog */}
      <Dialog open={promoOpen} onOpenChange={setPromoOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="promo-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>{promoEditing ? 'Edit promotion' : 'Schedule a promotion'}</DialogTitle>
            <DialogDescription>Set a temporary higher earning rate for a date range (e.g. “Double Points Weekend”).</DialogDescription>
          </DialogHeader>
          {!promoEditing && (
            <div className="flex flex-wrap gap-1.5 -mt-1 mb-1" data-testid="promo-dialog-templates">
              <span className="text-[10px] text-[#6B6B7A] self-center">Start from a template:</span>
              <button type="button" onClick={() => applyPromoTemplate('weekend')} className="text-[10px] font-semibold rounded-full border border-[#FFD9A8] bg-[#FFF4E6] text-[#946C00] px-2 py-1" data-testid="promo-dialog-template-weekend">⚡ Double Points Weekend</button>
              <button type="button" onClick={() => applyPromoTemplate('birthday')} className="text-[10px] font-semibold rounded-full border border-[#FBC6D6] bg-[#FFF0F4] text-[#B83258] px-2 py-1" data-testid="promo-dialog-template-birthday">🎂 Birthday Month Bonus</button>
              <button type="button" onClick={() => applyPromoTemplate('triple')} className="text-[10px] font-semibold rounded-full border border-[#C7D2FE] bg-[#EEF2FF] text-[#4F46E5] px-2 py-1" data-testid="promo-dialog-template-triple">✨ Triple Points Day</button>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label className="font-semibold">Name</Label>
              <Input value={promoForm.name} onChange={(e) => setPromoForm((p) => ({ ...p, name: e.target.value }))} placeholder="Double Points Weekend" className="mt-1 rounded-xl" data-testid="promo-name" />
            </div>
            <div>
              <Label className="font-semibold">Points per $1 (during promo)</Label>
              <Input type="number" min="0.1" step="0.1" value={promoForm.points_per_dollar} onChange={(e) => setPromoForm((p) => ({ ...p, points_per_dollar: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-rate" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="font-semibold">Start date</Label>
                <Input type="date" value={promoForm.start_date} onChange={(e) => setPromoForm((p) => ({ ...p, start_date: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-start" />
              </div>
              <div>
                <Label className="font-semibold">End date</Label>
                <Input type="date" value={promoForm.end_date} onChange={(e) => setPromoForm((p) => ({ ...p, end_date: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-end" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm bg-[#FFFDF8] rounded-xl p-3 border border-[#E8E5DF]">
              <Switch checked={promoForm.active} onCheckedChange={(v) => setPromoForm((p) => ({ ...p, active: v }))} data-testid="promo-active" />
              Active (uncheck to pause without deleting)
            </label>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPromoOpen(false)} className="flex-1 rounded-full" disabled={savingPromo}>Cancel</Button>
              <Button onClick={savePromo} disabled={savingPromo} className="flex-1 rounded-full bg-[#FF3B6B] text-white" data-testid="promo-save">
                {savingPromo ? 'Saving…' : (promoEditing ? 'Save' : 'Create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjust Points Dialog (reusable) */}
      <AdjustPointsDialog
        open={adjOpen}
        onOpenChange={(o) => { setAdjOpen(o); if (!o) setPresetCustomer(null); }}
        presetCustomer={presetCustomer}
        onSuccess={() => fetchAll()}
      />

      {/* Redeem an add-on for a customer */}
      <Dialog open={rfcOpen} onOpenChange={setRfcOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="rfc-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Redeem an add-on for a customer</DialogTitle>
            <DialogDescription>Spend a customer's points to add a free add-on to one of their upcoming parties.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="font-semibold">Customer email</Label>
              <div className="flex gap-2 mt-1">
                <Input value={rfcEmail} onChange={(e) => setRfcEmail(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') loadRfc(); }} placeholder="customer@email.com" className="rounded-xl" data-testid="rfc-email" />
                <Button onClick={loadRfc} disabled={rfcLoading} variant="outline" className="rounded-full border-[#E8E5DF]" data-testid="rfc-load">
                  {rfcLoading ? 'Loading…' : 'Look up'}
                </Button>
              </div>
            </div>

            {rfcCtx?.has_account && (
              <>
                <div className="rounded-xl bg-[#FFF4E6] border border-[#FFD9A8] px-3 py-2 text-sm text-[#B8860B] font-semibold flex items-center gap-1.5" data-testid="rfc-balance">
                  <Star className="w-4 h-4 text-[#E8A80E]" /> {(rfcCtx.name ? `${rfcCtx.name} — ` : '')}{(rfcCtx.balance || 0).toLocaleString()} pts available
                </div>
                {rfcCtx.eligible_bookings?.length === 0 ? (
                  <p className="text-sm text-red-500">This customer has no upcoming active bookings to apply a reward to.</p>
                ) : (
                  <>
                    <div>
                      <Label className="font-semibold">Apply to booking</Label>
                      <Select value={rfcBooking} onValueChange={setRfcBooking}>
                        <SelectTrigger className="mt-1 rounded-xl" data-testid="rfc-booking"><SelectValue placeholder="Choose a booking…" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {rfcCtx.eligible_bookings.map((b) => (
                            <SelectItem key={b.booking_id} value={b.booking_id}>{(b.child_name || 'Party')}'s party · {b.date}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="font-semibold">Add-on</Label>
                      <Select value={rfcReward} onValueChange={setRfcReward}>
                        <SelectTrigger className="mt-1 rounded-xl" data-testid="rfc-addon"><SelectValue placeholder="Choose an add-on…" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {(rfcCtx.addons || []).map((a) => (
                            <SelectItem key={a.reward_id} value={a.reward_id} disabled={!a.affordable}>
                              {a.name} — {a.points_cost} pts{!a.affordable ? ` (need ${a.deficit} more)` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button variant="outline" onClick={() => setRfcOpen(false)} className="flex-1 rounded-full" disabled={rfcBusy}>Cancel</Button>
                      <Button onClick={submitRfc} disabled={rfcBusy || !rfcBooking || !rfcReward} className="flex-1 rounded-full bg-[#FF3B6B] text-white" data-testid="rfc-submit">
                        {rfcBusy ? 'Applying…' : 'Redeem add-on'}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmLoyalty;
